/**
 * Product pricing mode -- the shared vocabulary (Jake, 2026-09-29).
 *
 * A campaign product is priced in exactly one of two ways:
 *   - "fixed"  -> shown as "Fixed price": the distributor sets the exact
 *                 price for each size (`fixed_size_prices`, integer cents).
 *   - "store"  -> shown as "Store pricing": the price comes from the
 *                 store's pricing tables, quoted by the server as
 *                 `price_preview` ({size: cents}).
 *
 * Every surface that names the mode (editor Pricing panel, editor header,
 * left rail, product card, products table) goes through this module so a
 * product cannot read one way in one place and another way elsewhere.
 *
 * Nothing here COMPUTES a price. The server is the only price source; the
 * helpers below only select among server-quoted cents (fromPriceCents) or
 * convert a person's typed dollars into the cents the PATCH carries.
 */
import { formatCurrency, parseCurrencyInput } from "@/utils/currency";
import {
  STANDARD_SIZES,
  NON_STANDARD_SIZES,
} from "@/pages/distributor/store-editor/StoreProducts/Editor/sizeConstants";

export const PRICING_MODE_FIXED = "fixed";
export const PRICING_MODE_STORE = "store";

const LABELS = Object.freeze({
  [PRICING_MODE_FIXED]: "Fixed price",
  [PRICING_MODE_STORE]: "Store pricing",
});

/** The contract value, or null. An unknown mode is never guessed. */
export function normalizePricingMode(value) {
  return value === PRICING_MODE_FIXED || value === PRICING_MODE_STORE
    ? value
    : null;
}

export function pricingModeLabel(mode) {
  return LABELS[normalizePricingMode(mode)] ?? null;
}

const SIZE_ORDER = [...STANDARD_SIZES, ...NON_STANDARD_SIZES];

/**
 * Selected sizes in display order: XS..5XL first, then any size outside
 * that set (OSFA, tall sizes, ...) in the order the product lists it.
 */
export function orderSizes(sizes) {
  const list = Array.isArray(sizes) ? sizes : [];
  const known = SIZE_ORDER.filter((s) => list.includes(s));
  const rest = list.filter((s) => !SIZE_ORDER.includes(s));
  return [...known, ...rest];
}

// A usable price in whole cents: an integer >= 0. Zero is a REAL price -- a
// fixed size can be free on purpose (Jake, 2026-09-30) -- so only null,
// absent, negative, or fractional values mean "no price".
function usableCents(value) {
  return Number.isInteger(value) && value >= 0;
}

/** True for a deliberate $0 price (a free size), which the editor flags. */
export function isFreeCents(value) {
  return value === 0;
}

/** The lowest server-quoted size price ("from $X"), or null. */
export function fromPriceCents(pricePreview) {
  if (!pricePreview || typeof pricePreview !== "object") return null;
  const values = Object.values(pricePreview).filter(usableCents);
  return values.length ? Math.min(...values) : null;
}

/**
 * The card/list label for a server quote: "$71.00" when every size costs
 * the same, "From $6.17" when sizes differ, null when nothing is quoted.
 */
export function fromPriceLabel(pricePreview) {
  const from = fromPriceCents(pricePreview);
  if (from === null) return null;
  const distinct = new Set(Object.values(pricePreview).filter(usableCents));
  const money = formatCurrency(from / 100);
  return distinct.size > 1 ? `From ${money}` : money;
}

/**
 * Typed dollars -> integer cents, or null when the text is not a usable
 * price (contract: an integer >= 0; "0" is a free size). Parsed from the digits rather than
 * `dollars * 100`, so float error cannot move a cent, and more than two
 * decimal places is rejected rather than rounded -- a fractional cent is
 * not a price anyone meant.
 */
export function parsePriceToCents(raw) {
  if (parseCurrencyInput(raw) === null) return null;
  const str = String(raw).trim();
  if (/^-|^\$-/.test(str)) return null;
  const cleaned = str.replace(/[$,\s]/g, "");
  const match = /^(\d*)(?:\.(\d{0,2}))?$/.exec(cleaned);
  if (!match || (match[1] === "" && !match[2])) return null;
  const cents =
    Number(match[1] || "0") * 100 + Number((match[2] || "").padEnd(2, "0"));
  return usableCents(cents) ? cents : null;
}

/** Cents -> the bare "12.50" an input shows, or "" when unset. */
export function centsToInputString(cents) {
  return usableCents(cents) ? (cents / 100).toFixed(2) : "";
}

/** Cents -> dollars for the shared currency formatter, or null. */
export function centsToDollars(cents) {
  return usableCents(cents) ? cents / 100 : null;
}

/** Selected sizes that lack a usable fixed price, in display order. */
export function missingPriceSizes(sizes, prices) {
  const map = prices && typeof prices === "object" ? prices : {};
  return orderSizes(sizes).filter((s) => !usableCents(map[s]));
}

/**
 * The selected sizes a FIXED-price product has no price for, in display
 * order; [] for store pricing or an unknown mode. A size added after the
 * prices were set is accepted unpriced, and a shopper cannot order it
 * (checkout 409s), so every surface that shows the product flags it.
 * `prices` is `fixed_size_prices` in the editor, or a list row's
 * `price_preview` (which, in fixed mode, holds exactly the priced sizes).
 */
export function unpricedFixedSizes(mode, sizes, prices) {
  if (normalizePricingMode(mode) !== PRICING_MODE_FIXED) return [];
  return missingPriceSizes(sizes, prices);
}

/** "1 size unpriced" / "3 sizes unpriced". */
export function unpricedSizesLabel(count) {
  return count === 1 ? "1 size unpriced" : `${count} sizes unpriced`;
}
